import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Sparkles,
  Zap,
  ChevronRight,
  AlertTriangle,
  Calendar,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { AppShell } from '@/components/layout/AppShell';
import { Header } from '@/components/layout/Header';
import { Card } from '@/components/ui/Card';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Skeleton, SkeletonCard } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';

/* ==========================================================================
   S2 — Dashboard (Home)

   Greeting, hero search bar, quick stats, critical alerts summary,
   recent sessions. NotificationBell is now rendered by Header.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Session types
   -------------------------------------------------------------------------- */

interface RecentSession {
  id: number;
  clientId: number;
  clientName: string;
  type: string;
  date: string;
  status: 'completed' | 'in_progress';
}

type SessionTab = 'in_progress' | 'completed' | 'overdue';

const SESSION_TABS: { key: SessionTab; label: string }[] = [
  { key: 'in_progress', label: 'In corso' },
  { key: 'completed', label: 'Completate' },
  { key: 'overdue', label: 'Urgenti' },
];

/* --------------------------------------------------------------------------
   Stat card data
   -------------------------------------------------------------------------- */

interface StatItem {
  label: string;
  value: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
}

/* --------------------------------------------------------------------------
   Alert summary types
   -------------------------------------------------------------------------- */

interface AlertSummary {
  laserInactive: number;
  checkupDue: number;
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // ------------------------------------------------------------------
  // Alert summary (critical notifications count)
  // ------------------------------------------------------------------

  const [alertSummary, setAlertSummary] = useState<AlertSummary | null>(null);

  const loadAlertSummary = useCallback(async () => {
    try {
      const data = await api.get<{ data: Array<{ type: string }> }>(
        '/notifications?limit=100',
      );
      const notifications = data?.data ?? [];
      const laserInactive = notifications.filter(
        (n) => n.type === 'laser_inactive' && !('readAt' in n && (n as { readAt?: string }).readAt),
      ).length;
      const checkupDue = notifications.filter(
        (n) => n.type === 'checkup_due' && !('readAt' in n && (n as { readAt?: string }).readAt),
      ).length;
      setAlertSummary({ laserInactive, checkupDue });
    } catch {
      // Silently ignore — alerts are non-critical on dashboard
    }
  }, []);

  useEffect(() => {
    loadAlertSummary();
  }, [loadAlertSummary]);

  // ------------------------------------------------------------------
  // Session tabs (In corso / Completate / Urgenti)
  // ------------------------------------------------------------------

  const [activeTab, setActiveTab] = useState<SessionTab>('in_progress');
  const [sessions, setSessions] = useState<RecentSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  const loadSessions = useCallback(async (tab: SessionTab) => {
    setSessionsLoading(true);
    try {
      const data = await api.get<{ data: RecentSession[] }>(
        `/dashboard/sessions?status=${tab}&limit=20`,
      );
      setSessions(data?.data ?? []);
    } catch {
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions(activeTab);
  }, [loadSessions, activeTab]);

  // ------------------------------------------------------------------
  // Stats giornaliere (check-up oggi + sessioni laser oggi)
  // ------------------------------------------------------------------

  const [statsData, setStatsData] = useState<{ checkupsOggi: number; laserOggi: number } | null>(null);

  useEffect(() => {
    api
      .get<{ checkupsOggi: number; laserOggi: number }>('/dashboard/stats')
      .then(setStatsData)
      .catch(() => setStatsData({ checkupsOggi: 0, laserOggi: 0 }));
  }, []);

  // ------------------------------------------------------------------
  // Greeting based on time of day
  // ------------------------------------------------------------------

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Buongiorno';
    if (hour < 18) return 'Buon pomeriggio';
    return 'Buonasera';
  }, []);

  // Today's formatted date
  const todayDate = useMemo(() => {
    return new Intl.DateTimeFormat('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(new Date());
  }, []);

  const stats: StatItem[] = [
    {
      label: 'Check-up oggi',
      value: statsData ? String(statsData.checkupsOggi) : '—',
      icon: Sparkles,
      color: 'text-accent-primary',
      bgColor: 'bg-accent-primary-light',
    },
    {
      label: 'Sessioni laser',
      value: statsData ? String(statsData.laserOggi) : '—',
      icon: Zap,
      color: 'text-accent-secondary',
      bgColor: 'bg-accent-secondary-light',
    },
  ];

  // ------------------------------------------------------------------
  // Show alert summary only if there are alerts
  // ------------------------------------------------------------------

  const hasAlerts =
    alertSummary !== null &&
    (alertSummary.laserInactive > 0 || alertSummary.checkupDue > 0);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <AppShell header={<Header />}>
      {/* Greeting */}
      <div className="mb-6">
        <h1 className="font-[family-name:var(--font-heading)] text-2xl font-extrabold text-text-primary tracking-tight">
          {greeting}, {user?.nome ?? 'Operatrice'}
        </h1>
        <p className="mt-1 text-sm text-text-muted capitalize">{todayDate}</p>
      </div>

      {/* ---- HERO: Search bar (navigates to /search on tap) ---- */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/search')}
          className={cn(
            'relative w-full rounded-[var(--radius-xl)]',
            'border border-border-default bg-surface-card',
            'shadow-sm',
            'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
            'hover:border-border-hover hover:shadow-md',
            'active:shadow-sm',
            'cursor-pointer',
            'text-left',
          )}
        >
          <Search
            className={cn(
              'absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5',
              'text-text-muted',
            )}
            strokeWidth={1.5}
            aria-hidden
          />
          <div
            className={cn(
              'w-full h-14 pl-13 pr-5',
              'flex items-center',
              'text-base text-text-muted',
              'font-[family-name:var(--font-body)]',
            )}
          >
            Cerca cliente per nome, cognome o telefono...
          </div>
        </button>
      </div>

      {/* ---- Quick stats ---- */}
      <div className="mb-8 grid grid-cols-3 gap-3">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card
              key={stat.label}
              padding="sm"
              shadow="sm"
              className="text-center"
            >
              <div
                className={cn(
                  'mx-auto mb-2 flex h-10 w-10 items-center justify-center',
                  'rounded-[var(--radius-md)]',
                  stat.bgColor,
                )}
              >
                <Icon className={cn('h-5 w-5', stat.color)} strokeWidth={1.5} />
              </div>
              <p className="font-[family-name:var(--font-heading)] text-xl font-bold text-text-primary tabular-nums">
                {stat.value}
              </p>
              <p className="text-xs text-text-muted mt-0.5 leading-tight">{stat.label}</p>
            </Card>
          );
        })}
      </div>

      {/* ---- Critical alert summary ---- */}
      {hasAlerts && (
        <section className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-[family-name:var(--font-heading)] text-base font-bold text-text-primary">
              Avvisi critici
            </h2>
            <button
              onClick={() => navigate('/notifications')}
              className={cn(
                'flex items-center gap-1 text-sm font-medium text-accent-primary',
                'hover:text-accent-primary-hover transition-colors cursor-pointer',
              )}
            >
              Vedi tutte
              <ChevronRight className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {alertSummary!.laserInactive > 0 && (
              <AlertCard
                icon={AlertTriangle}
                iconClass="text-amber-500"
                bgClass="bg-amber-50"
                count={alertSummary!.laserInactive}
                label="Clienti laser inattivi"
                onClick={() => navigate('/notifications')}
              />
            )}
            {alertSummary!.checkupDue > 0 && (
              <AlertCard
                icon={Calendar}
                iconClass="text-status-info"
                bgClass="bg-status-info-light"
                count={alertSummary!.checkupDue}
                label="Check-up in scadenza"
                onClick={() => navigate('/notifications')}
              />
            )}
          </div>
        </section>
      )}

      {/* ---- Sessions with tabs ---- */}
      <section>
        <h2 className="font-[family-name:var(--font-heading)] text-lg font-bold text-text-primary mb-4">
          Sessioni
        </h2>

        {/* Tab bar */}
        <div className="flex gap-2 mb-4">
          {SESSION_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'px-4 py-2 rounded-[var(--radius-full)]',
                'text-sm font-[family-name:var(--font-heading)] font-semibold',
                'transition-all duration-[var(--duration-fast)]',
                'cursor-pointer',
                activeTab === tab.key
                  ? tab.key === 'overdue'
                    ? 'bg-status-negative text-white'
                    : 'bg-accent-primary text-white'
                  : 'bg-surface-sunken text-text-secondary hover:bg-border-default',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Session list */}
        {sessionsLoading ? (
          <div className="flex gap-3 overflow-hidden pb-2 -mx-5 px-5">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height={120} className="w-56 shrink-0 rounded-[var(--radius-lg)]" />
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div className="flex flex-col items-center py-8 text-center">
            <p className="text-sm text-text-muted">
              {activeTab === 'in_progress' && 'Nessuna sessione in corso'}
              {activeTab === 'completed' && 'Nessuna sessione completata'}
              {activeTab === 'overdue' && 'Nessuna sessione urgente'}
            </p>
          </div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-5 px-5 scrollbar-hide">
            {sessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                isOverdue={activeTab === 'overdue'}
                onClick={() => navigate(`/clients/${session.clientId}`)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Bottom spacer for safe-area */}
      <div className="h-8" />
    </AppShell>
  );
}

/* --------------------------------------------------------------------------
   Alert Card — compact card for critical alert types
   -------------------------------------------------------------------------- */

function AlertCard({
  icon: Icon,
  iconClass,
  bgClass,
  count,
  label,
  onClick,
}: {
  icon: LucideIcon;
  iconClass: string;
  bgClass: string;
  count: number;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 px-4 py-3',
        'rounded-[var(--radius-lg)] border border-border-default bg-surface-card',
        'transition-all duration-[var(--duration-fast)]',
        'hover:border-border-hover hover:shadow-sm',
        'cursor-pointer text-left w-full',
        'active:scale-[0.98]',
      )}
    >
      <div
        className={cn(
          'flex items-center justify-center h-9 w-9 rounded-full shrink-0',
          bgClass,
        )}
      >
        <Icon className={cn('h-4.5 w-4.5', iconClass)} strokeWidth={1.5} />
      </div>
      <div className="min-w-0">
        <p className="font-[family-name:var(--font-heading)] text-xl font-bold text-text-primary">
          {count}
        </p>
        <p className="text-xs text-text-muted leading-tight truncate">{label}</p>
      </div>
    </button>
  );
}

/* --------------------------------------------------------------------------
   Session card — used in horizontal scroll
   -------------------------------------------------------------------------- */

function SessionCard({
  session,
  isOverdue = false,
  onClick,
}: {
  session: RecentSession;
  isOverdue?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'shrink-0 w-56 text-left',
        'rounded-[var(--radius-lg)] border bg-surface-card',
        'p-4 shadow-xs',
        'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
        'hover:shadow-sm hover:-translate-y-0.5',
        'cursor-pointer',
        'active:translate-y-0 active:shadow-xs',
        isOverdue ? 'border-status-negative/30' : 'border-border-default',
      )}
    >
      <div className="flex items-center gap-3 mb-3">
        <Avatar name={session.clientName} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-primary truncate">
            {session.clientName}
          </p>
          <p className="text-xs text-text-muted truncate">{session.type}</p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-xs text-text-muted">{session.date}</span>
        <Badge
          status={
            isOverdue
              ? 'negative'
              : session.status === 'completed'
                ? 'positive'
                : 'info'
          }
          size="sm"
          dot
        >
          {isOverdue
            ? 'Urgente'
            : session.status === 'completed'
              ? 'Completata'
              : 'In corso'}
        </Badge>
      </div>
    </button>
  );
}

/* --------------------------------------------------------------------------
   Dashboard skeleton (exported for use during loading)
   -------------------------------------------------------------------------- */

export function DashboardSkeleton() {
  return (
    <AppShell header={<Header hideNotificationBell />}>
      <div className="space-y-6">
        <div>
          <Skeleton height={28} className="w-3/5 mb-2" />
          <Skeleton height={16} className="w-2/5" />
        </div>

        <Skeleton height={56} className="w-full rounded-[var(--radius-xl)]" />

        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>

        <div>
          <Skeleton height={22} className="w-2/5 mb-4" />
          <div className="flex gap-3 overflow-hidden">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height={120} className="w-56 shrink-0 rounded-[var(--radius-lg)]" />
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
